import { NodeType } from 'src/modules/exam-preparation/graphql/common/types/node';
import db from '../../dexie-db/db';
import NetworkInfo from '../NetworkInfo';
import ImageCacheFactory from './ImageCacheFactory';
import { ImageCacheStatus } from './types';
function isUnsettledStatus(status, includeInProgressImages = false) {
    return !status || [ImageCacheStatus.NOT_CACHED, ImageCacheStatus.FAILED, includeInProgressImages ? ImageCacheStatus.CACHING : null].includes(status);
}
function isStaleCache(node) {
    const status = node.data?.imageCacheInfo?.status;
    return Boolean(status === ImageCacheStatus.CACHED
        && node.data?.imageCacheInfo?.cachedAt
        && new Date(node.data?.imageCacheInfo?.cachedAt).getTime() < new Date(node.utime).getTime());
}
/**
 * Extracts image URLs from HTML content, excluding data:base64 embedded images
 */
function extractImageURLsFromHTML(htmlContent) {
    if (!htmlContent)
        return [];
    const imgRegex = /<img[^>]+src=["']([^"']+)["'][^>]*>/gi;
    const imageURLs = [];
    let match;
    while ((match = imgRegex.exec(htmlContent)) !== null) {
        const url = match[1];
        // Skip data:base64 embedded images
        if (url && !url.startsWith('data:')) {
            imageURLs.push(url);
        }
    }
    return imageURLs;
}
function getImageURLs(node) {
    if (node.type === NodeType.DIAGRAM) {
        return node.data?.imageURL ? [node.data.imageURL] : [];
    }
    if ([NodeType.WRITTEN_QUESTION, NodeType.ORALS_QUESTION].includes(node.type)) {
        const questionNode = node;
        const questionHTML = questionNode.data?.questionHTML;
        if (questionHTML) {
            return extractImageURLsFromHTML(questionHTML);
        }
        return [];
    }
    return [];
}
export class ImageCacheManager {
    cacheAdapter = ImageCacheFactory.getInstance();
    isProcessing = false;
    static MAX_CACHE_RETRIES = 3;
    networkInfo;
    constructor() {
        this.networkInfo = new NetworkInfo();
    }
    async processPendingImages(includeInProgressImages = false) {
        if (__DEV__ || this.isProcessing || !this.cacheAdapter.isSupported())
            return;
        this.isProcessing = true;
        try {
            const pendingNodes = await db.qnaNode
                .where('type')
                .anyOf([NodeType.DIAGRAM, NodeType.WRITTEN_QUESTION, NodeType.ORALS_QUESTION])
                .and(node => {
                return isUnsettledStatus(node.data?.imageCacheInfo?.status, includeInProgressImages) || isStaleCache(node);
            })
                .toArray();
            for (const node of pendingNodes) {
                await this.networkInfo.waitForOnline();
                await this.cacheDiagramNode(node);
            }
        }
        catch (err) {
        }
        finally {
            this.isProcessing = false;
        }
    }
    async cacheDiagramNode(node) {
        const imageURLs = getImageURLs(node);
        const prevRetryCount = node.data?.imageCacheInfo?.retryCount || 0;
        if (imageURLs.length === 0 || prevRetryCount >= ImageCacheManager.MAX_CACHE_RETRIES)
            return;
        await this.updateImageCacheInfo(node, {
            status: ImageCacheStatus.CACHING,
            lastAttempt: new Date(),
            retryCount: prevRetryCount + 1,
        });
        try {
            // Cache all images for this node
            const cachePromises = imageURLs.map(url => this.cacheAdapter.cacheImage(url));
            const results = await Promise.all(cachePromises);
            const allSuccessful = results.every(success => success);
            if (allSuccessful) {
                await this.updateImageCacheInfo(node, {
                    status: ImageCacheStatus.CACHED,
                    cachedAt: new Date(),
                    retryCount: prevRetryCount + 1,
                });
            }
            else {
                throw new Error('One or more images failed to cache');
            }
        }
        catch (error) {
            await this.updateImageCacheInfo(node, {
                status: ImageCacheStatus.FAILED,
                lastAttempt: new Date(),
                errorMessage: error?.message || 'Unknown error',
                retryCount: prevRetryCount + 1,
            });
        }
    }
    /**
     * Delete cached images for nodes with the specified syncBoxId
     * @param syncBoxId - The syncBoxId to filter nodes
     */
    async deleteCachedImagesForSyncBox(syncBoxId) {
        if (!this.cacheAdapter.isSupported())
            return;
        try {
            const nodes = await db.qnaNode
                .where('type')
                .anyOf([NodeType.DIAGRAM, NodeType.WRITTEN_QUESTION, NodeType.ORALS_QUESTION])
                .and(node => node._syncBoxId === syncBoxId)
                .toArray();
            for (const node of nodes) {
                const imageURLs = getImageURLs(node);
                if (imageURLs.length > 0) {
                    // Uncache all images for this node
                    const uncachePromises = imageURLs.map(url => this.cacheAdapter.uncacheImage(url));
                    await Promise.all(uncachePromises);
                    // Clear the image cache info
                    await this.updateImageCacheInfo(node, {
                        status: ImageCacheStatus.NOT_CACHED,
                        lastAttempt: new Date(),
                        retryCount: 0,
                    });
                }
            }
        }
        catch (error) {
            console.error('Error deleting cached images for syncBox:', error);
        }
    }
    async updateImageCacheInfo(node, info) {
        await db.qnaNode.update(node._id, item => {
            item.data.imageCacheInfo = info;
        });
    }
}
export const imageCacheManager = new ImageCacheManager();
